import { ArrowRightOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Divider, Flex, Input, Typography, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux-hooks";
import { useFuseSearch } from "../../../hooks/useFuseSearch";
import { createCustomNote } from "../../../service/notes.service";
import { setAITemplateModalState, setCustomTemplateSelectedTab, setSelectedView } from "../../../slices/appStateSlice";
import styles from "./AITemplateModal.module.scss";
import List from "./List";
import TemplateListContainer from "./TemplateListContainer.component";
import { type CustomNote, TabType, ViewType } from "./types";

// Module-level so the Fuse index isn't rebuilt on every render.
const SEARCH_KEYS = ["name"];

// CustomNote.name is a 100-char column; the create endpoint rejects anything longer.
const NAME_MAX_LENGTH = 100;

/** "<name> (Copy)", then "(Copy 2)", "(Copy 3)"... The stem is trimmed so the suffix
 * always fits, otherwise a long name would come back as the name it was copied from. */
function copyNameFor(sourceName: string, takenNames: Set<string>) {
  let candidate = sourceName;
  for (let attempt = 1; attempt <= 50; attempt++) {
    const suffix = attempt === 1 ? " (Copy)" : ` (Copy ${attempt})`;
    candidate = `${sourceName.slice(0, NAME_MAX_LENGTH - suffix.length).trimEnd()}${suffix}`;
    if (!takenNames.has(candidate)) break;
  }
  return candidate;
}

const ListContainer = ({
  handleEditTemplate,
  customNoteList,
  fetchCustomNoteList,
  isListLoading,
}: {
  setIsPrimaryButtonDisabled?: (disabled: boolean) => void;
  handleEditTemplate: (template: CustomNote) => void;
  isListLoading: boolean;
  customNoteList: CustomNote[];
  fetchCustomNoteList: () => void;
}) => {
  const componentName = "ai-template-modal";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  // pk of the template whose copy is in flight — keyed rather than a bare boolean so only
  // the clicked card's button spins, and a second click can't create a third copy.
  const [duplicatingPk, setDuplicatingPk] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const navigateToTemplateGallery = () => {
    dispatch(setAITemplateModalState(false));
    dispatch(setSelectedView(ViewType.TEMPLATE));
    navigate("/templates");
  };

  function Header() {
    return (
      <>
        <div className={styles[`${componentName}__header`]}>
          <div className={styles[`${componentName}__header-container`]}>
            <button
              onClick={() => dispatch(setAITemplateModalState(false))}
              className={styles[`${componentName}__header-close-button`]}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                width={20}
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>

            <h3>My Templates</h3>
          </div>
          <Button
            type="primary"
            onClick={() => dispatch(setCustomTemplateSelectedTab(TabType.CREATE))}
            icon={<PlusOutlined />}
            aria-label="Create Template"
            title="Create Template"
          >
            Create Template
          </Button>
        </div>
        <Divider style={{ marginBlock: "0" }} />
      </>
    );
  }

  const userOwnedCustomNotes = customNoteList.filter((note) => note.is_owner);
  const sharedCustomNotes = customNoteList.filter((note) => !note.is_owner);

  // A duplicate is just a new template of your own holding copied content, so it goes
  // through the ordinary create endpoint: that already allocates the type_id, stamps the
  // owner from the session and kicks off the coding-instructions parse. It also keeps the
  // "which templates may this user see" rule in one place on the server (available_note_types)
  // instead of giving it a second home a duplicate-specific action would have to match.
  async function handleDuplicateTemplate(template: CustomNote) {
    if (duplicatingPk !== null) return;
    setDuplicatingPk(template.pk);
    // The copy is always private: sharing with the group is an admin's deliberate act,
    // never something a duplicate inherits.
    const settings = template.is_owner
      ? template.settings
      : // Same provenance stamp the fork-on-edit path writes (AITemplateModal.component).
        { ...template.settings, forked_from_custom_note_id: template.pk };
    try {
      await createCustomNote({
        name: copyNameFor(template.name, new Set(userOwnedCustomNotes.map((note) => note.name))),
        document: template.document,
        prompt: template.prompt,
        coding_instructions: template.coding_instructions,
        from_template: template.from_template,
        is_group_accessible: false,
        settings,
      });
      message.success(`"${template.name}" was duplicated`);
      await fetchCustomNoteList();
    } catch (error) {
      message.error("Failed to duplicate template");
      console.error(error);
    } finally {
      setDuplicatingPk(null);
    }
  }

  // One Fuse over the whole list (own + group-shared) so a query matches templates in either
  // section; the sections are re-derived from the match set below to keep their original order.
  const searchableNotes = useMemo(
    () => customNoteList.map((note) => ({ pk: note.pk, name: note.name })),
    [customNoteList],
  );
  const { search: fuseSearch } = useFuseSearch({ data: searchableNotes, keys: SEARCH_KEYS });

  const isSearching = debouncedQuery.length > 0;
  const matchedPks = isSearching ? new Set(fuseSearch(debouncedQuery).map((note) => note.pk)) : null;

  const visibleOwnedNotes = matchedPks
    ? userOwnedCustomNotes.filter((note) => matchedPks.has(note.pk))
    : userOwnedCustomNotes;
  const visibleSharedNotes = matchedPks
    ? sharedCustomNotes.filter((note) => matchedPks.has(note.pk))
    : sharedCustomNotes;
  const hasCustomNoteMatches = visibleOwnedNotes.length > 0 || visibleSharedNotes.length > 0;

  return (
    <>
      <Header />
      <div className={styles[`${componentName}__search`]}>
        <Input
          placeholder="Search templates..."
          prefix={<SearchOutlined style={{ color: "#666" }} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
        />
      </div>
      {/* While searching, a section with no matches is dropped entirely — showing "My Templates"
          with the create-your-first empty state under a query reads as "you have none". */}
      {(!isSearching || visibleOwnedNotes.length > 0) && (
        <>
          <div className={styles[`${componentName}__list-header`]}>My Templates</div>
          <List
            handleEditTemplate={handleEditTemplate}
            handleDuplicateTemplate={handleDuplicateTemplate}
            duplicatingPk={duplicatingPk}
            customNoteList={visibleOwnedNotes}
            fetchCustomNoteList={fetchCustomNoteList}
            isListLoading={isListLoading}
            isCompact={isSearching}
          />
        </>
      )}
      {visibleSharedNotes.length > 0 && (
        <>
          <div className={styles[`${componentName}__list-header`]} style={{ marginTop: "20px" }}>
            Shared with me
          </div>
          <List
            handleEditTemplate={handleEditTemplate}
            handleDuplicateTemplate={handleDuplicateTemplate}
            duplicatingPk={duplicatingPk}
            customNoteList={visibleSharedNotes}
            fetchCustomNoteList={fetchCustomNoteList}
            isListLoading={isListLoading}
            isCompact={isSearching}
            isSharedList={true}
          />
        </>
      )}
      {isSearching && !hasCustomNoteMatches && !isListLoading && (
        <div className={styles[`${componentName}__search-empty`]}>
          <Typography.Text type="secondary">No templates of yours match "{debouncedQuery}"</Typography.Text>
        </div>
      )}
      <Flex className={styles[`${componentName}__list-header-cont`]} style={{ marginTop: "20px" }}>
        <div className={styles[`${componentName}__list-header`]}>TEMPLATE GALLERY</div>
        <Button onClick={navigateToTemplateGallery} className={styles[`${componentName}__list-header-button`]}>
          200+ templates | View all templates <ArrowRightOutlined />
        </Button>
      </Flex>
      <TemplateListContainer searchQuery={debouncedQuery} />
    </>
  );
};

export default ListContainer;
